import { useAuthorizationSignature } from '@privy-io/react-auth';
import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { PORTFOLIO_QUERY_KEYS } from '@/api/portfolio/queries';
import { type TransactionSubmission } from '@/api/wallet/queries';
import { useWalletAccess } from '@/features/wallet-access/wallet-access-provider';
import { type WalletOperation } from '@/features/wallet-access/wallet-operation';

import { useUserSuiAddress } from '../use-user-sui-address';
import { SendAssertionError } from './assert-send-tx';
import { clearPendingSend, readPendingSend, savePendingSend } from './pending-send';
import { reconcileSubmittedTransaction } from './reconcile-submitted-transaction';
import {
  failedSendState,
  IDLE_SEND_STATE,
  progressSendState,
  SendStatus,
  successfulSendState,
  UNCERTAIN_SEND_STATE,
} from './send-state';
import {
  buildAuthorizationRequest,
  createVerifiedIntent,
  PENDING_SUBMISSION,
  type SendInput,
  signAuthorizationRequest,
  submitOrAssumePending,
} from './send-transfer';
import { awaitSubmittedDigest, RejectedSubmissionError } from './submission-status';
import { type SendAttempt, useSendAttempts } from './use-send-attempts';

interface PendingTransfer {
  senderAddress: string;
  intentId: string;
}

interface AuthorizedTransfer extends PendingTransfer {
  signature: string;
}

export function useSend() {
  const senderAddress = useUserSuiAddress();
  const { state, beginAttempt, reset } = useSendAttempts(senderAddress);
  const authorize = useTransferAuthorization();
  const settle = useTransferSettlement();

  const checkStatus = useCallback(async () => {
    const attempt = beginAttempt();
    const intentId = senderAddress ? readPendingSend(senderAddress) : null;

    if (!senderAddress || !intentId) {
      attempt.update(IDLE_SEND_STATE);
      return undefined;
    }

    return settle(attempt, { senderAddress, intentId }, PENDING_SUBMISSION);
  }, [beginAttempt, senderAddress, settle]);

  const send = useCallback(
    async (input: SendInput) => {
      const attempt = beginAttempt();
      attempt.update(progressSendState(SendStatus.PREPARING));
      const transfer = await authorize(attempt, input, senderAddress).catch((error: unknown) =>
        failSend(attempt, error),
      );

      if (!transfer) {
        return checkStatus();
      }

      const submission = await submitOrAssumePending(transfer.intentId, transfer.signature);
      return settle(attempt, transfer, submission);
    },
    [authorize, beginAttempt, checkStatus, senderAddress, settle],
  );

  return { send, reset, checkStatus, ...state };
}

function useTransferAuthorization() {
  const { generateAuthorizationSignature } = useAuthorizationSignature();
  const { beginOperation, getWalletAccessToken } = useWalletAccess();

  return useCallback(
    async (
      attempt: SendAttempt,
      input: SendInput,
      address: string | undefined,
    ): Promise<AuthorizedTransfer | null> => {
      const senderAddress = requireSenderAddress(address);

      if (readPendingSend(senderAddress)) {
        return null;
      }

      const operation = beginOperation();
      const assertCurrent = assertAttemptAndOperation(attempt, operation);
      const intent = await operation.run(() => createVerifiedIntent(input));
      attempt.update(progressSendState(SendStatus.AWAITING_SIGNATURE));
      const request = await buildAuthorizationRequest(intent, input, senderAddress);
      assertCurrent();
      const signature = await signAuthorizationRequest(request, {
        generateAuthorizationSignature,
        refreshWalletSession: getWalletAccessToken,
        assertCurrent,
      });
      assertCurrent();
      savePendingSend(senderAddress, intent.intentId);
      return { senderAddress, intentId: intent.intentId, signature };
    },
    [beginOperation, generateAuthorizationSignature, getWalletAccessToken],
  );
}

function useTransferSettlement() {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      attempt: SendAttempt,
      transfer: PendingTransfer,
      submission: TransactionSubmission,
    ): Promise<string> => {
      attempt.update(progressSendState(SendStatus.SUBMITTING));

      try {
        const digest = await awaitSubmittedDigest(transfer.intentId, submission);
        clearPendingSend(transfer.senderAddress, transfer.intentId);
        await refreshWalletPortfolioBestEffort(queryClient);
        attempt.update(successfulSendState(digest));
        void reconcileSubmittedTransaction(queryClient, digest).catch(() => undefined);
        return digest;
      } catch (error) {
        recordSettlementFailure(attempt, transfer, error);
        throw error;
      }
    },
    [queryClient],
  );
}

function assertAttemptAndOperation(attempt: SendAttempt, operation: WalletOperation) {
  return () => {
    attempt.assertCurrent();
    operation.assertCurrent();
  };
}

function requireSenderAddress(address: string | undefined): string {
  if (!address) {
    throw new Error('Your wallet is not ready yet.');
  }

  return address;
}

function failSend(attempt: SendAttempt, error: unknown): never {
  if (error instanceof SendAssertionError) {
    Sentry.captureException(error, {
      tags: { security_control: 'coin_transfer_intent_verification' },
    });
  }

  attempt.update(failedSendState(error));
  throw error;
}

function recordSettlementFailure(
  attempt: SendAttempt,
  transfer: PendingTransfer,
  error: unknown,
): void {
  if (error instanceof RejectedSubmissionError) {
    clearPendingSend(transfer.senderAddress, transfer.intentId);
    attempt.update(failedSendState(error));
    return;
  }

  attempt.update(UNCERTAIN_SEND_STATE);
}

async function refreshWalletPortfolioBestEffort(queryClient: QueryClient): Promise<void> {
  await queryClient
    .invalidateQueries({ queryKey: [PORTFOLIO_QUERY_KEYS.walletPortfolio] })
    .catch(() => undefined);
}
